/**
 * The report-download modal's public surface: a lazy wrapper (CWS-196).
 *
 * The modal's implementation lives in ``EmailCaptureModalDialog.tsx`` inside
 * the ``lead-modals`` chunk, fetched on the visitor's **first open** —
 * CWS-179 measured the form libraries it carries (~234 kB of ``zod`` +
 * ``react-hook-form``) as the entry bundle's LCP lever, paid by every first
 * visit for a dialog most visitors never open. This module must never
 * statically import the dialog module: the only edge to it is the
 * ``import()`` below, and ``tests-node/leadModalChunkGuard.test.ts`` fails
 * the build if a form library resurfaces in an entry-path chunk.
 *
 * Two wrapper behaviours are load-bearing, and both are pinned by the modal
 * suites:
 *
 * * **The first-open latch.** Nothing renders until the first open; from then
 *   on the dialog stays mounted across close → reopen for the life of the
 *   route mount — its opening counter, draft and confirmation state live in
 *   the component instance (``SYSTEM_DESIGN.md`` §15.9, L-o/L-q; L-r keeps
 *   navigation-away covered via ``mountedRef``). Conditional
 *   ``{open && …}`` mounting would destroy them on every close.
 * * **The failure boundary.** A first open whose chunk fetch fails is
 *   contained by ``LeadModalBoundary`` (fallback ``null``, one console
 *   trace, one session-latched recovery reload) instead of unmounting the
 *   app. See ``LeadModalBoundary.tsx`` for why a reload is the only honest
 *   recovery and how the latch bounds it.
 */

import { lazy, Suspense, useEffect, useState } from "react";
import { LeadModalBoundary } from "@/components/LeadModalBoundary";
import type { EmailCaptureModalProps } from "./EmailCaptureModalDialog";

const EmailCaptureModalDialog = lazy(() => import("./EmailCaptureModalDialog"));

export function EmailCaptureModal(props: EmailCaptureModalProps) {
  const [everOpened, setEverOpened] = useState(false);

  // The latch reads `open` directly: the wrapper mounts the lazy child on the
  // first open and never unmounts it again for the route mount's life.
  useEffect(() => {
    if (props.open) {
      setEverOpened(true);
    }
  }, [props.open]);

  if (!everOpened) return null;

  return (
    <LeadModalBoundary>
      <Suspense fallback={null}>
        <EmailCaptureModalDialog {...props} />
      </Suspense>
    </LeadModalBoundary>
  );
}
